/**
 * AuthContext - Shared Authentication state and actions
 *
 * Manages:
 * - Authentication state (loading, authenticated, unauthenticated, etc.)
 * - Login, logout, register, confirm registration
 * - Auth-related error/success messages
 */

import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import {
  login as authLogin,
  logout as authLogout,
  register as authRegister,
  confirmRegistration,
  getUser
} from '../auth';

// Auth state machine
export type AuthState = 'loading' | 'unauthenticated' | 'needs_confirmation' | 'needs_parental_consent' | 'authenticated';

interface AuthContextType {
  // State
  authState: AuthState;
  error: string;
  success: string;
  isLoading: boolean;

  // Computed
  isAuthenticated: boolean;

  // Actions
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  register: (
    email: string,
    password: string,
    options?: { givenName?: string; familyName?: string; birthDate?: string; inviteToken?: string }
  ) => Promise<{ success: boolean; autoConfirmed: boolean }>;
  confirmCode: (email: string, code: string) => Promise<boolean>;
  clearMessages: () => void;
  setError: (error: string) => void;
  setSuccess: (success: string) => void;
  setAuthState: (state: AuthState) => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [authState, setAuthState] = useState<AuthState>('loading');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Check auth on mount
  useEffect(() => {
    checkAuth();
  }, []);

  async function checkAuth() {
    try {
      const user = await getUser();
      if (user) {
        setAuthState('authenticated');
      } else {
        setAuthState('unauthenticated');
      }
    } catch {
      setAuthState('unauthenticated');
    }
  }

  const login = useCallback(async (email: string, password: string): Promise<boolean> => {
    setError('');
    try {
      await authLogin(email, password);
      setAuthState('authenticated');
      return true;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Login failed';
      setError(message);
      return false;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await authLogout();
    } finally {
      setAuthState('unauthenticated');
    }
  }, []);

  const register = useCallback(async (
    email: string,
    password: string,
    options?: { givenName?: string; familyName?: string; birthDate?: string; inviteToken?: string }
  ): Promise<{ success: boolean; autoConfirmed: boolean }> => {
    setError('');
    try {
      const result = await authRegister(email, password, options);
      // Check if user was auto-confirmed (invite-based signup)
      const autoConfirmed = result.isSignUpComplete;
      if (autoConfirmed) {
        // User is auto-confirmed, they can log in immediately
        setSuccess('Account created! Signing you in...');
        // Don't change auth state yet - let the caller handle login
      } else {
        // Normal flow - needs email confirmation
        setAuthState('needs_confirmation');
        setSuccess('Check your email for a confirmation code');
      }
      return { success: true, autoConfirmed };
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Registration failed';
      setError(message);
      return { success: false, autoConfirmed: false };
    }
  }, []);

  const confirmCode = useCallback(async (email: string, code: string): Promise<boolean> => {
    setError('');
    try {
      await confirmRegistration(email, code);
      setAuthState('unauthenticated');
      setSuccess('Email confirmed! You can now log in.');
      return true;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Confirmation failed';
      setError(message);
      return false;
    }
  }, []);

  const clearMessages = useCallback(() => {
    setError('');
    setSuccess('');
  }, []);

  const value: AuthContextType = {
    authState,
    error,
    success,
    isLoading: authState === 'loading',
    isAuthenticated: authState === 'authenticated',
    login,
    logout,
    register,
    confirmCode,
    clearMessages,
    setError,
    setSuccess,
    setAuthState,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
